import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { useShopStore } from "../store/shop.store";

export default function EtsyCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "existing" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");

    if (error) {
      setStatus("error");
      setMessage(`Etsy yetkilendirme hatası: ${error}`);
      return;
    }

    if (!code || !state) {
      setStatus("error");
      setMessage("Geçersiz callback parametreleri");
      return;
    }

    // Exchange code for tokens via our backend
    async function exchangeToken() {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL || "http://localhost:3001"}/api/etsy/oauth/callback`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("erc_token") || ""}`,
            },
            body: JSON.stringify({ code, state }),
          }
        );

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || "Token alınamadı");
        }

        const data = await res.json();
        const { shop_name, shopRecordId, isNew } = data.data as {
          shop_name?: string; shopRecordId?: string; isNew?: boolean;
        };

        // Yeni baglanan magazayi listeye al ve aktif yap
        await useShopStore.getState().loadShops();
        if (shopRecordId) useShopStore.getState().setActiveShop(shopRecordId);

        if (isNew === false) {
          // Zaten bagli bir magaza dondu. Kullanici YENI magaza baglamak
          // istediyse sebebi neredeyse her zaman Etsy oturumunun hala onceki
          // hesapta olmasidir — sessizce "basarili" demek yaniltici olur.
          setStatus("existing");
          setMessage(shop_name || "");
        } else {
          setStatus("success");
          setMessage(`Mağaza bağlandı: ${shop_name || "Başarılı"}`);
        }

        // Redirect to settings after 2 seconds
        setTimeout(() => navigate("/settings"), isNew === false ? 6000 : 2000);
      } catch (err: any) {
        setStatus("error");
        setMessage(err.message || "Bağlantı hatası");
      }
    }

    exchangeToken();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm text-center max-w-md">
        {status === "loading" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">Etsy hesabı bağlanıyor...</h2>
            <p className="mt-2 text-sm text-gray-500">Lütfen bekleyin</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">Başarılı!</h2>
            <p className="mt-2 text-sm text-gray-500">{message}</p>
            <p className="mt-1 text-xs text-gray-400">Ayarlara yönlendiriliyorsunuz...</p>
          </>
        )}
        {status === "existing" && (
          <>
            <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-amber-500" />
            <h2 className="text-lg font-semibold text-gray-900">Bu mağaza zaten bağlıydı</h2>
            <p className="mt-2 text-sm text-gray-600">
              <b>{message}</b> mağazasının yetkisi tazelendi. Yeni bir mağaza bağlamak
              istediyseniz bağlantı kurulmadı.
            </p>
            <p className="mt-3 rounded-lg bg-amber-50 p-3 text-left text-xs text-amber-800">
              Sebep: tarayıcınız hâlâ bu Etsy hesabına girişli. Farklı bir mağaza için
              Etsy'den çıkış yapın veya <b>gizli sekmede</b> diğer hesapla giriş yapıp
              tekrar deneyin.
            </p>
            <p className="mt-2 text-xs text-gray-400">Ayarlara yönlendiriliyorsunuz...</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">Bağlantı Hatası</h2>
            <p className="mt-2 text-sm text-red-600">{message}</p>
            <button
              onClick={() => navigate("/settings")}
              className="mt-4 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Ayarlara Dön
            </button>
          </>
        )}
      </div>
    </div>
  );
}
